import {
  Box,
  Text,
  WizardStep,
  WizardStepRenderParams,
  useOnNext,
} from '@factorialco/deprecated-design-system'
import isEmpty from 'lodash/isEmpty'

import TextArea from 'modules/core/components/TextArea'
import TextField from 'modules/core/components/TextField'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useCreateProcessFromTemplate } from 'modules/processes/hooks/useCreateProcessFromTemplate'
import type { WizardData } from 'modules/processes/pages/Modals/ProcessFromTemplateWizardPage/ProcessFromTemplateWizard/types'

const DetailsStep = ({ data, onDataChanged, loading }: WizardStepRenderParams<WizardData>) => {
  const { createProcess } = useCreateProcessFromTemplate()

  const { performAction } = useAsyncAction(async (data: WizardData) => {
    const result = await createProcess({
      name: data.processName!,
      description: data.processDescription,
      templateId: data.template.id,
      category: data.template.category,
    })

    const handled = handleResult(result, (errors) => {
      if (errors) {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('errors.generic_saving'),
        })
      }
    })

    if (handled.success && handled.data.process) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })

      onDataChanged({
        ...data,
        processId: handled.data?.process?.id,
      })
    }
  })

  useOnNext(performAction)

  return (
    <Box gap="s24" width="s360">
      <Text.Header2>{i18n.t('processes.process_from_template.steps.details.title')}</Text.Header2>
      <TextField
        mandatory
        label={i18n.t('processes.process_from_template.steps.details.name')}
        placeholder={i18n.t('processes.process_from_template.steps.details.name_placeholder')}
        value={data.processName ?? ''}
        onChange={(value) => onDataChanged({ ...data, processName: value })}
        readonly={loading}
      />
      <TextArea
        label={i18n.t('processes.process_from_template.steps.details.description')}
        placeholder={i18n.t(
          'processes.process_from_template.steps.details.description_placeholder'
        )}
        value={data.processDescription ?? ''}
        onChange={(value) => onDataChanged({ ...data, processDescription: value })}
        readonly={loading}
      />
    </Box>
  )
}

export const detailsStep = (): WizardStep<WizardData> => ({
  title: i18n.t('processes.process_from_template.steps.details.step_name'),
  nextText: i18n.t('processes.process_from_template.submit'),
  render: (props) => <DetailsStep {...props} />,
  isCompleted: (data) => !isEmpty(data.processName?.trim()),
})
